import { forwardRef, useId, type InputHTMLAttributes, type ReactNode } from 'react';
import { normalizeIndianPhoneEntry } from '../../utils/phone';
import styles from './PhoneInput.module.css';

type NativeProps = Omit<
  InputHTMLAttributes<HTMLInputElement>,
  'value' | 'onChange' | 'type' | 'inputMode' | 'maxLength'
>;

interface Props extends NativeProps {
  /** 10 national digits (or the stripped text while incomplete or invalid). */
  value: string;
  onValueChange: (value: string) => void;
  /** Shown under the field and linked with aria-describedby. */
  error?: string | null;
  /** Optional status shown inside the field, after the input (e.g. a lookup state). */
  trailing?: ReactNode;
}

/**
 * Indian mobile number entry with a fixed +91 prefix. Pasted formats such as
 * "+91 98765 43210" are normalised; there is deliberately no maxLength, so the
 * browser never cuts a paste short before it can be normalised.
 */
export const PhoneInput = forwardRef<HTMLInputElement, Props>(function PhoneInput(
  { value, onValueChange, error, trailing, id, className, ...rest },
  ref,
) {
  const generatedId = useId();
  const inputId = id ?? generatedId;
  const errorId = `${inputId}-error`;
  return (
    <div>
      <div className={`${styles.group} ${error ? styles.invalid : ''}`}>
        <span className={styles.prefix} aria-hidden="true">+91</span>
        <input
          {...rest}
          ref={ref}
          id={inputId}
          className={`${styles.input} ${className ?? ''}`}
          type="tel"
          inputMode="numeric"
          autoComplete="tel-national"
          value={value}
          onChange={(e) => onValueChange(normalizeIndianPhoneEntry(e.target.value))}
          aria-invalid={error ? true : undefined}
          aria-describedby={error ? errorId : undefined}
        />
        {trailing && <span className={styles.trailing}>{trailing}</span>}
      </div>
      {error && (
        <p id={errorId} className={styles.error} role="alert">
          {error}
        </p>
      )}
    </div>
  );
});
